import { RefObject, useLayoutEffect } from 'react';
import useAlignOnOpen from './useAlignOnOpen';
import usePaneFitHeight from './usePaneFitHeight';
import { rowOf } from './scrollPane';

/**
 * How far down the page an open row reaches: most of it, with the next row's
 * edge still showing below so the list plainly goes on.
 */
export const ROW_PANE_SHARE = 0.95;

/**
 * The rules for a row that opens in place onto a body — a change instance, a
 * service in the catalogue, a webhook response. Every list whose rows open
 * this way calls this, so they all behave alike:
 *
 * - opened (by a click, or by a link), the row comes up the page until the row
 *   before it sits at the top, still readable, and the open row under it;
 * - it fills the page down to ROW_PANE_SHARE of it — its body gets that height
 *   as `--open-panel-h` on the card, for the body's CSS (`nx-open-row__panel`
 *   does it, and scrolls inside);
 * - a wheel over it while it is open scrolls only its body, never the page;
 *   anywhere else, the page scrolls, the open row with it;
 * - closed, it stays where it is, and the rows below come up under it;
 * - while it is open it carries `data-open-row`, which OpenRowHint finds it
 *   by and a wheel over the nav leaves alone (useWheelToContent), and its
 *   list gets the room at its foot that the last rows need to reach the top
 *   — what ROW_PANE_SHARE leaves of the page.
 *
 * `headRef` is the row's own line, the part that stays the same height open
 * or closed. `rowSelector` names the list item the card sits in where that is
 * not the card itself (antd's Timeline wraps each card in one).
 *
 * `neighbourShare` is how much of the rows either side stays in view: all of
 * the one before and the edge of the one after by default; a share instead —
 * 0.5 for the catalogue's tall cards — shows that much of each, the lower part
 * of the one above and the upper part of the one below.
 */
const useExpandedRow = (
    cardRef: RefObject<HTMLElement>,
    headRef: RefObject<HTMLElement>,
    open: boolean,
    {
        deepLinked,
        rowSelector,
        neighbourShare,
    }: { deepLinked?: boolean; rowSelector?: string; neighbourShare?: number } = {},
) => {
    // before the height below: the row's place on the page decides how much of
    // the page is left for it
    useAlignOnOpen(cardRef, open, deepLinked, { revealPrevious: neighbourShare ?? true, rowSelector });
    const panelH = usePaneFitHeight(cardRef, headRef, open, ROW_PANE_SHARE, rowSelector, neighbourShare);

    useLayoutEffect(() => {
        const card = cardRef.current;
        if (!open || !card) return undefined;
        const row = rowOf(card, rowSelector);
        const list = row.parentElement;
        row.setAttribute('data-open-row', '');
        // a little more than the share leaves: the pane is the window less
        // whatever is above it, so a share of the window covers it
        list?.style.setProperty('padding-bottom', `${Number(((1 - ROW_PANE_SHARE) * 100).toFixed(2))}vh`);
        return () => {
            row.removeAttribute('data-open-row');
            list?.style.removeProperty('padding-bottom');
        };
    }, [open, cardRef, rowSelector]);

    useLayoutEffect(() => {
        const card = cardRef.current;
        if (!card) return;
        if (open && panelH) card.style.setProperty('--open-panel-h', `${panelH}px`);
        else card.style.removeProperty('--open-panel-h');
    }, [open, panelH, cardRef]);
};

export default useExpandedRow;
